/**
 * Parse the pipe-delimited RAW universe string into typed rows (plan §3.3).
 * Fail-closed: bad sector, blank name, or non-finite numerics throw.
 */
import { SectorIdSchema, asTicker, type SectorId, type Ticker } from '@meridian/contracts';

import { SECN } from '../constants/sectors';
import { UNIVERSE_RAW } from '../constants/universe.raw';

export interface RawUniverseRow {
  readonly sc: SectorId;
  readonly ticker: Ticker;
  readonly name: string;
  readonly sector: string;
  readonly mcap: number;
  readonly pe: number;
  readonly div: number;
  readonly rev: number;
}

/**
 * `min` applies the same domain bound the wire schema does, at the byte
 * boundary. Without it core's parser is looser than the contract it feeds:
 * a negative mcap or rev parses here and only fails later at
 * SecurityFundamentalsSchema, turning a fixture defect into a wire error.
 */
function parseFinite(raw: string, field: string, lineNo: number, min?: number): number {
  if (raw.trim() === '') {
    throw new Error(`universe RAW line ${String(lineNo)}: empty ${field}`);
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    throw new Error(
      `universe RAW line ${String(lineNo)}: non-finite ${field} (${JSON.stringify(raw)})`,
    );
  }
  if (min !== undefined && n < min) {
    throw new Error(
      `universe RAW line ${String(lineNo)}: ${field} below ${String(min)} (${String(n)})`,
    );
  }
  return n;
}

function parseLine(line: string, lineNo: number): RawUniverseRow {
  const parts = line.trim().split('|');
  if (parts.length !== 7) {
    throw new Error(
      `universe RAW line ${String(lineNo)}: expected 7 fields, got ${String(parts.length)}`,
    );
  }
  const [scRaw, tickerRaw, nameRaw, mc, pe, dv, rv] = parts as [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
  ];
  /* Wrap so every rejection carries the line number: a bare ZodError here would
     be the one failure path a reader cannot locate in the source. */
  const sc = ((): SectorId => {
    try {
      return SectorIdSchema.parse(scRaw);
    } catch {
      throw new Error(
        `universe RAW line ${String(lineNo)}: unknown sector code (${JSON.stringify(scRaw)})`,
      );
    }
  })();
  const name = nameRaw.trim();
  if (name.length === 0) {
    throw new Error(`universe RAW line ${String(lineNo)}: empty name`);
  }
  return {
    sc,
    ticker: asTicker(tickerRaw),
    name,
    sector: SECN[sc],
    /* Bounds mirror SecurityFundamentalsSchema: mcap/div/rev nonnegative, pe
       unbounded (a negative P/E is meaningful — loss-making earnings). */
    mcap: parseFinite(mc, 'mcap', lineNo, 0),
    pe: parseFinite(pe, 'pe', lineNo),
    div: parseFinite(dv, 'div', lineNo, 0),
    rev: parseFinite(rv, 'rev', lineNo, 0),
  };
}

/** Parse RAW (default: committed UNIVERSE_RAW) into ordered rows. */
export function parseUniverse(raw: string = UNIVERSE_RAW): readonly RawUniverseRow[] {
  /* Number lines BEFORE filtering blanks: numbering after the filter reports a
     position in the surviving rows, not in the source the reader is looking at. */
  return raw
    .trim()
    .split('\n')
    .map((line, i) => [line, i + 1] as const)
    .filter(([line]) => line.trim().length > 0)
    .map(([line, lineNo]) => parseLine(line, lineNo));
}
